import { IAndroidConfig } from "./android-config";
import { IApiConfig } from "./api-configuration";
import { ICommonSettings } from "./common-config";
import { IElectronConfig } from "./electron-config";
import { IIosConfig } from "./ios-config";
import { EMobileGenerationType } from "./mobile-generation-type";
import { IWebConfig } from "./web-config";

export interface IConfig {
  id: string;
  name: string;
  globalConfigurationId?: string;
  supportAndroid: boolean;
  supportIOS: boolean;
  supportWeb: boolean;
  supportElectron: boolean;
  isSelected: boolean;
  isRunConfig: boolean;
  configurationVersion: number;
  publishMethods: string | null;
  /**
   * Legacy single setting that covered both mobile platforms. Superseded by
   * `androidGenerationType` / `iosGenerationType`, which a customer can point at
   * different generators. Still read as the fallback for configurations saved
   * before the split; undefined means Native.
   *
   * @deprecated Read `androidGenerationType` / `iosGenerationType` instead.
   */
  mobileGenerationType?: EMobileGenerationType;
  /**
   * Which generator produces this configuration's Android application. Undefined on
   * configurations saved before the split — fall back to `mobileGenerationType`.
   */
  androidGenerationType?: EMobileGenerationType;
  /**
   * Which generator produces this configuration's iOS application. Undefined on
   * configurations saved before the split — fall back to `mobileGenerationType`.
   */
  iosGenerationType?: EMobileGenerationType;
  apiConfig: IApiConfig;
  webConfig: IWebConfig;
  androidConfig: IAndroidConfig | null;
  iosConfig: IIosConfig | null;
  electronConfig: IElectronConfig | null;
  isGlobalConfigOnProjectConfigList?: boolean;
  isKuikaCloud?: boolean;
}

export interface GetConfigsRequestDTO {
  configurations: IConfig[];
  commonSettings: ICommonSettings;
}

/**
 * Which generator produces this configuration's app for one mobile platform, resolving the legacy single
 * setting. `mobileGenerationType` covered both platforms before they were split; a configuration saved
 * before the split carries only that, and one saved before EITHER existed carries neither — which means
 * Native, the behaviour those configurations were saved under.
 */
function resolveMobileGenerationType(
  platformType: EMobileGenerationType | undefined,
  legacyType: EMobileGenerationType | undefined
): EMobileGenerationType {
  return platformType ?? legacyType ?? EMobileGenerationType.Native;
}

/**
 * Whether any of these configurations produces a **React Native** app for a mobile platform it publishes.
 *
 * <p>The TS mirror of the backend's `CodeGenerationOptions.GenerateReactNativeForMobile`, which asks the
 * same question per publish run: is a platform selected AND does its generation type route to React Native.
 * "Any configuration" rather than the selected one, because the designer is one canvas for every
 * configuration a project has — a screen styled here is generated by all of them.</p>
 *
 * <p>Read by surfaces that must only offer what the project's targets can actually render: the styling
 * panel's Shadow and Effects sections are hidden on a mobile project whose apps are all Native, because
 * neither the Android (Kotlin) nor the iOS (Swift) generator emits a shadow or a transform at all.</p>
 */
export function generatesReactNativeForMobile(configurations: readonly IConfig[] | undefined): boolean {
  if (configurations === undefined) {
    return false;
  }
  return configurations.some((config: IConfig): boolean => {
    const android: boolean =
      config.supportAndroid === true &&
      resolveMobileGenerationType(config.androidGenerationType, config.mobileGenerationType) ===
        EMobileGenerationType.ReactNative;
    const ios: boolean =
      config.supportIOS === true &&
      resolveMobileGenerationType(config.iosGenerationType, config.mobileGenerationType) ===
        EMobileGenerationType.ReactNative;
    return android || ios;
  });
}
